import React, { useEffect, useState } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	FireScreenAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { DEFAULT_SIMILAR_INCIDENTS_CARD, MAX_SIMILAR_INCIDENTS_CARD } from '../../common/constants';
import type { TProps, TSimilarIncident } from '../../common/types';
import { useRelatedIncidentsForIssue } from '../../services/context';
import SimilarIncidentsFeedback from './feedback';
import SimilarIncidentsPanelHeader from './header';
import messages from './messages';
import SimilarIncidentsCard from './similar-incident-card';
import SkeletonLoadingContainer from './skeleton';

export const SimilarIncidentsPanelView = ({ sessionId, defaultExpanded }: TProps) => {
	const [isExpanded, setExpanded] = useState(Boolean(defaultExpanded));
	const [showMore, setShowMore] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [{ similarIncidentData }, { fetchRelatedIncidentsForIssue }] =
		useRelatedIncidentsForIssue();

	useEffect(() => {
		async function fetchData() {
			await fetchRelatedIncidentsForIssue(createAnalyticsEvent);
			setIsLoading(false);
		}
		if (isExpanded) {
			fetchData();
		}
	}, [fetchRelatedIncidentsForIssue, createAnalyticsEvent, isExpanded]);

	const handleShowMore = () => {
		setShowMore(true);
		fireUIAnalytics(createAnalyticsEvent({}), 'showMoreButton clicked', {
			suggestionId: similarIncidentData.suggestionId,
		});
	};

	const renderSimilarIncidentsBody = () => (
		<>
			{similarIncidentData && similarIncidentData.similarIssues.length ? (
				<FieldsContainer data-testid="smart-related-issues-panel.ui.similar-incidents.similar-incidents-container">
					<FireScreenAnalytics />
					{similarIncidentData.similarIssues
						.slice(0, DEFAULT_SIMILAR_INCIDENTS_CARD)
						.map((curr: TSimilarIncident, index: number) => (
							<SimilarIncidentsCard
								suggestionId={similarIncidentData.suggestionId}
								currentIncident={curr}
								rank={index}
								key={index}
							/>
						))}
					{showMore &&
						similarIncidentData.similarIssues
							.slice(DEFAULT_SIMILAR_INCIDENTS_CARD, MAX_SIMILAR_INCIDENTS_CARD)
							.map((curr: TSimilarIncident, index: number) => (
								<SimilarIncidentsCard
									suggestionId={similarIncidentData.suggestionId}
									currentIncident={curr}
									rank={index}
									key={index}
								/>
							))}
					{!showMore && similarIncidentData.similarIssues.length > 3 && (
						<Button
							appearance="link"
							onClick={handleShowMore}
							interactionName="auto-generated.jira-smart-related-issues-panel.ui.similar-incidents.button"
						>
							{formatMessage(messages.showMoreButton)}
						</Button>
					)}
					<SimilarIncidentsFeedback
						sessionId={sessionId}
						suggestionId={similarIncidentData.suggestionId}
					/>
				</FieldsContainer>
			) : (
				<FieldsContainer data-testid="smart-related-issues-panel.ui.similar-incidents.similar-incidents-container">
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p> {formatMessage(messages.noSimilarIncidents)} </p>
				</FieldsContainer>
			)}
		</>
	);

	const renderSimilarIncidents = () => (
		<>
			<SimilarIncidentsPanelHeader
				sessionId={sessionId}
				onExpand={() => setExpanded(true)}
				onCollapse={() => {
					setShowMore(false);
					setExpanded(false);
				}}
				isExpanded={isExpanded}
			/>
			{isExpanded && isLoading && <SkeletonLoadingContainer />}
			{isExpanded && !isLoading && <PanelBody>{renderSimilarIncidentsBody()}</PanelBody>}
		</>
	);

	return <SimilarIncidentsContainer>{renderSimilarIncidents()}</SimilarIncidentsContainer>;
};

export default SimilarIncidentsPanelView;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SimilarIncidentsContainer = styled.div<{
	hasContentItems?: boolean;
}>({
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PanelBody = styled.div({
	marginBottom: token('space.300', '24px'),
	display: 'flex',
	flexDirection: 'column',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderBottomLeftRadius: `${gridSize / 2}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderBottomRightRadius: `${gridSize / 2}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderBottom: `1px solid ${token('color.border', colors.N40)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderLeft: `1px solid ${token('color.border', colors.N40)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderRight: `1px solid ${token('color.border', colors.N40)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface', colors.N0),
	paddingLeft: token('space.100', '8px'),
	paddingRight: token('space.100', '8px'),
	paddingBottom: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldsContainer = styled.div({
	overflow: 'hidden',
});
